import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { ThemeProvider } from './contexts/ThemeContext';

// Components
import AuthPortal from './components/AuthPortal';
import Dashboard from './components/Dashboard';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import ToastProvider from './components/ToastNotifications';
import ClientOnboarding from './components/ClientOnboarding';
import OnboardingTutorial from './components/OnboardingTutorial';
import ChatBot from './components/ChatBot';
import UserAuthorization from './components/UserAuthorization';
import SubscriptionManager from './components/SubscriptionManager';

// Feature Components
import Analytics from './components/AdAnalytics';
import Content from './components/SimplePosting';
import Calendar from './components/SimplePosting';
import LeadCapture from './components/LeadCapture';
import UnifiedLeadIntelligence from './components/UnifiedLeadIntelligence';
import SmartFollowUp from './components/SmartFollowUpSystem';
import RevenueIntelligence from './components/RevenueIntelligence';
import CSVImport from './components/CSVImport';
import AIInsights from './components/LeadAI';
import AIAgent from './components/AIAgentDashboard';
import Training from './components/TrainingSystem';
import Settings from './components/ClientDashboard';

// Mobile Components
import MobileLayout from './components/mobile/MobileLayout';

// Desktop Components
import DesktopDashboard from './components/desktop/DesktopDashboard';

// Platform detection
import { usePlatform } from './hooks/shared/usePlatform';



function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState('client');
  const [sidebarOpen, setSidebarOpen] = useState(true); // Auto-open sidebar on desktop
  const [isLoading, setIsLoading] = useState(true);
  const [authorizationStatus, setAuthorizationStatus] = useState(null);
  const [showAuthorization, setShowAuthorization] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);

  // Platform detection using new system
  const { isMobile, isDesktop, platform } = usePlatform();

  useEffect(() => {
    console.log('App.js: useEffect running');
    setIsLoading(true);
    
    const clientToken = localStorage.getItem('clientToken');
    const clientData = localStorage.getItem('clientData');
    
    console.log('App.js: Tokens found:', { clientToken: !!clientToken, clientData: !!clientData });
    
    if (clientToken && clientData) {
      try {
        const userData = JSON.parse(clientData);
        console.log('App.js: Setting authenticated user:', userData);
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('App.js: Error parsing user data:', error);
        localStorage.removeItem('clientToken');
        localStorage.removeItem('clientData');
      }
    }
    
    setIsLoading(false);
    console.log('App.js: useEffect complete, isAuthenticated:', isAuthenticated);
  }, []);

  // Facebook Login Status Check
  useEffect(() => {
    // Check if Facebook SDK is loaded
    if (window.FB) {
      console.log('Facebook SDK loaded, checking login status...');
      
      window.FB.getLoginStatus(function(response) {
        console.log('Facebook login status:', response);
        
        if (response.status === 'connected') {
          console.log('User is connected to Facebook and our app');
          console.log('Access token:', response.authResponse.accessToken);
          console.log('User ID:', response.authResponse.userID);
          
          // Store Facebook access token
          localStorage.setItem('facebookAccessToken', response.authResponse.accessToken);
          localStorage.setItem('facebookUserID', response.authResponse.userID);
          
        } else if (response.status === 'not_authorized') {
          console.log('User is logged into Facebook but not our app');
        } else {
          console.log('User is not logged into Facebook');
        }
      });
    } else {
      console.log('Facebook SDK not loaded yet');
    }
  }, []);

  const handleLogin = (userData, token) => {
    console.log('App.js: handleLogin called with:', userData);
    localStorage.setItem('clientToken', token);
    localStorage.setItem('clientData', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
    
    // Check authorization after login
    checkUserAuthorization(userData);
  };

  const checkUserAuthorization = (userData) => {
    const userProfile = userData?.user_metadata || {};
    const subscriptionTier = userProfile.subscription_tier || 'free';
    const subscriptionStatus = userProfile.subscription_status || 'inactive';
    const isPaymentVerified = userProfile.payment_verified || false;
    const isTrialActive = userProfile.trial_ends_at ? new Date(userProfile.trial_ends_at) > new Date() : false;
    
    // Show authorization screen if payment not verified for paid plans and not on trial
    if (subscriptionTier !== 'free' && !isPaymentVerified && !isTrialActive) {
      setShowAuthorization(true);
    }
  };

  const handleAuthorized = (userData, status) => {
    setAuthorizationStatus(status);
    setShowAuthorization(false);
    // User is now authorized to access the dashboard
  };

  const handleUnauthorized = (userData, status) => {
    setAuthorizationStatus(status);
    setShowSubscription(true);
  };

  const handleSubscriptionUpdated = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('clientData', JSON.stringify(updatedUser));
    setShowSubscription(false);
    setShowAuthorization(false);
    // User subscription updated, now authorized
  };

  const handleProfileUpdated = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('clientData', JSON.stringify(updatedUser));
  };

  const handleLogout = () => {
    localStorage.removeItem('clientToken');
    localStorage.removeItem('clientData');
    setUser(null);
    setIsAuthenticated(false);
  };

  console.log('App.js: Rendering, isLoading:', isLoading, 'isAuthenticated:', isAuthenticated, 'platform:', platform);

  if (isLoading) {
    return (
      <ThemeProvider>
        <CssBaseline />
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          backgroundColor: 'background.default'
        }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  if (!isAuthenticated) {
    return (
      <ThemeProvider>
        <CssBaseline />
        <Box sx={{ 
          minHeight: '100vh',
          backgroundColor: 'background.default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <AuthPortal onLogin={handleLogin} />
        </Box>
      </ThemeProvider>
    );
  }

  // Show authorization screen if needed
  if (showAuthorization) {
    return (
      <ThemeProvider>
        <CssBaseline />
        <UserAuthorization 
          user={user} 
          onAuthorized={handleAuthorized} 
          onUnauthorized={handleUnauthorized} 
        />
      </ThemeProvider>
    );
  }

  // Show subscription management if needed
  if (showSubscription) {
    return (
      <ThemeProvider>
        <CssBaseline />
        <SubscriptionManager 
          user={user} 
          onSubscriptionUpdated={handleSubscriptionUpdated} 
        />
      </ThemeProvider>
    );
  }

  // Mobile Layout
  if (isMobile) {
    return (
      <ThemeProvider>
        <CssBaseline />
        <ToastProvider>
          <Router>
            <MobileLayout isAuthenticated={isAuthenticated}>
              <Routes>
                <Route path="/" element={<Dashboard user={user} />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/content" element={<Content />} />
                <Route path="/calendar" element={<Calendar />} />
                <Route path="/lead-capture" element={<LeadCapture />} />
                <Route path="/unified-leads" element={<UnifiedLeadIntelligence />} />
                <Route path="/smart-follow-up" element={<SmartFollowUp />} />
                <Route path="/revenue-intelligence" element={<RevenueIntelligence />} />
                <Route path="/csv-import" element={<CSVImport />} />
                <Route path="/ai-insights" element={<AIInsights />} />
                <Route path="/ai-agent" element={<AIAgent />} />
                <Route path="/training" element={<Training />} />
                <Route path="/settings" element={<Settings user={user} onProfileUpdated={handleProfileUpdated} />} />
                <Route path="/onboarding" element={<ClientOnboarding />} />
                <Route path="/tutorial" element={<OnboardingTutorial onComplete={() => window.location.href = '/onboarding'} onSkip={() => window.location.href = '/onboarding'} />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <ChatBot />
            </MobileLayout>
          </Router>
        </ToastProvider>
      </ThemeProvider>
    );
  }

  // Desktop Layout
  return (
    <ThemeProvider>
      <CssBaseline />
      <ToastProvider>
        <Router>
          <Box sx={{ display: 'flex', height: '100vh' }}>
            <Sidebar open={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
            
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Header onLogout={handleLogout} user={user} onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
              
              <Box component="main" sx={{ 
                flexGrow: 1,
                pt: { xs: 7, sm: 8 },
                pl: { xs: 0.25, sm: 1 },
                pr: { xs: 0.25, sm: 1 },
                backgroundColor: 'background.default',
                overflowY: 'auto',
                overflowX: 'hidden'
              }}>
                <Routes>
                  <Route path="/" element={<DesktopDashboard user={user} />} />
                  <Route path="/analytics" element={<Analytics />} />
                  <Route path="/content" element={<Content />} />
                  <Route path="/calendar" element={<Calendar />} />
                  <Route path="/lead-capture" element={<LeadCapture />} />

                <Route path="/unified-leads" element={<UnifiedLeadIntelligence />} />
                <Route path="/smart-follow-up" element={<SmartFollowUp />} />
                  <Route path="/revenue-intelligence" element={<RevenueIntelligence />} />
                  <Route path="/csv-import" element={<CSVImport />} />
                  <Route path="/ai-insights" element={<AIInsights />} />
                  <Route path="/ai-agent" element={<AIAgent />} />
                  <Route path="/training" element={<Training />} />
                  <Route path="/settings" element={<Settings user={user} onProfileUpdated={handleProfileUpdated} />} />
                  <Route path="/onboarding" element={<ClientOnboarding />} />
                  <Route path="/tutorial" element={<OnboardingTutorial onComplete={() => window.location.href = '/onboarding'} onSkip={() => window.location.href = '/onboarding'} />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Box>
            </Box>
            <ChatBot />
          </Box>
        </Router>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
